import AddIcon from "@mui/icons-material/Add";
import {
	Alert,
	Box,
	Button,
	Card,
	CircularProgress,
	Container,
	List,
	Snackbar,
	Stack,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import HockeyPlannerRoot from "@/frontend/components/HockeyPlannerRoot";
import type { ProfileFormData } from "@/frontend/components/ProfileDialog";
import ProfileDialog from "@/frontend/components/ProfileDialog";
import {
	createFrontendTrpcClient,
	formatCurrency,
	formatDeDateRange,
	usePerKeyDebounce,
} from "@/frontend/utils";
import { calculateCostPerParticipantFromTotals } from "@/frontend/views/payments-import/matching";
import type {
	EventRecord,
	ParticipationStatus,
} from "@/types/event-attendance";
import type { ParticipationRecord, UserInfo } from "./types";
import { UserRow } from "./UserRow";

const trpcClient = createFrontendTrpcClient();

export default function ParticipationView() {
	const { eventId: eventIdStr } = useParams<{ eventId: string }>();
	const eventId = eventIdStr ? parseInt(eventIdStr, 10) : null;
	if (!eventId || Number.isNaN(eventId)) {
		return (
			<HockeyPlannerRoot title="Teilnehmer" showBackButton={true}>
				<Container maxWidth="md">
					<Typography color="error">Ungültige Event-ID</Typography>
				</Container>
			</HockeyPlannerRoot>
		);
	} else {
		return <ParticipationViewInner eventId={eventId} />;
	}
}

function ParticipationViewInner({ eventId }: { eventId: number }) {
	const qc = useQueryClient();

	// Fetch event details
	const { data: eventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ["events", { includePast: true }],
		queryFn: () => trpcClient.event.get.query({ includePast: true }),
	});
	const event = eventsData?.events?.find((e: EventRecord) => e.id === eventId);

	// Fetch participation data for this event
	const { data: participationData, isLoading: participationLoading } = useQuery(
		{
			queryKey: ["event-participation", eventId],
			queryFn: () => trpcClient.event.getEventParticipation.query({ eventId }),
			enabled: !!eventId,
		},
	);

	// Fetch all users
	const { data: allUsersData, isLoading: usersLoading } = useQuery({
		queryKey: ["all-users"],
		queryFn: () => trpcClient.user.getAll.query(),
	});

	const allUsers: UserInfo[] = (allUsersData?.users ?? []).map((u) => ({
		...u,
		firstname: u.firstname ?? "",
		lastname: u.lastname ?? "",
	}));
	const participations: ParticipationRecord[] = (
		participationData?.participations ?? []
	).map((p) => ({
		...p,
		firstname: p.firstname ?? "",
		lastname: p.lastname ?? "",
	}));

	// Build state maps
	const participationMap = useMemo(() => {
		const map: Record<string, ParticipationStatus> = {};
		for (const p of participations) {
			map[p.user_id] = (p.status as ParticipationStatus) || undefined;
		}
		return map;
	}, [participations]);

	// Get attendance data to separate registered vs non-registered users
	const attendanceSet = useMemo(() => {
		const set = new Set<string>();
		if (event?.attendance_details) {
			for (const detail of event.attendance_details) {
				if (detail.attends === "yes") set.add(String(detail.userid));
			}
		}
		return set;
	}, [event?.attendance_details]);

	// Separate users into two groups
	const registeredUsers = useMemo<UserInfo[]>(() => {
		if (!event?.attendance_details) return [];
		const userMap = new Map(allUsers.map((u) => [u.user_id, u]));
		return event.attendance_details
			.filter((detail) => detail.attends === "yes")
			.map((detail) => userMap.get(String(detail.userid)))
			.filter((user): user is UserInfo => user !== undefined);
	}, [allUsers, event?.attendance_details]);

	const unregisteredUsers = useMemo(() => {
		return allUsers.filter((u) => !attendanceSet.has(u.user_id));
	}, [allUsers, attendanceSet]);

	// Track pending updates per user
	const [pendingByUserId, setPendingByUserId] = useState<
		Record<string, boolean>
	>({});
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [newParticipantDialogOpen, setNewParticipantDialogOpen] =
		useState(false);
	const { schedule } = usePerKeyDebounce(500);

	// Calculate number of paying players (yes and goalie, but not goalies for cost split)
	const payingPlayerCount = useMemo(() => {
		return Object.entries(participationMap).filter(
			([, status]) => status === "yes",
		).length;
	}, [participationMap]);

	// Calculate per-player cost
	const costPerPlayer = useMemo(() => {
		if (!event || payingPlayerCount === 0) return 0;
		return (
			calculateCostPerParticipantFromTotals(
				event.total_cost_cents,
				payingPlayerCount,
			) ?? 0
		);
	}, [event, payingPlayerCount]);

	const updateParticipationMutation = useMutation({
		mutationFn: async (vars: {
			userStatuses: Record<string, ParticipationStatus>;
		}) =>
			trpcClient.event.updateEventParticipation.mutate({ eventId, ...vars }),
		onError: (err) => setErrorMsg(err?.message ?? "Fehler beim Speichern"),
		onSettled: async () => {
			await qc.invalidateQueries({
				queryKey: ["event-participation", eventId],
			});
		},
	});

	const createParticipantMutation = useMutation({
		mutationFn: async (formData: ProfileFormData) =>
			trpcClient.event.createParticipantAndAddToEvent.mutate({
				eventId,
				email: formData.email,
				displayname: formData.displayname,
				firstname: formData.firstname,
				lastname: formData.lastname,
				mobile: formData.mobile,
				participationStatus: "yes",
			}),
		onError: (err) => setErrorMsg(err?.message ?? "Fehler beim Erstellen"),
		onSettled: async () => {
			await Promise.all([
				qc.invalidateQueries({
					queryKey: ["event-participation", eventId],
				}),
				qc.invalidateQueries({
					queryKey: ["all-users"],
				}),
			]);
		},
	});

	const handleStatusChange = useCallback(
		(userId: string, status: ParticipationStatus | null) => {
			setPendingByUserId((s) => ({ ...s, [userId]: true }));
			schedule(userId, async () => {
				try {
					const userStatuses: Record<string, ParticipationStatus> = {};
					if (status !== null) {
						userStatuses[userId] = status;
						await updateParticipationMutation.mutateAsync({ userStatuses });
					}
				} catch (e: unknown) {
					const message =
						e instanceof Error
							? e.message
							: typeof e === "string"
								? e
								: String(e ?? "Fehler beim Speichern");
					setErrorMsg(message);
				} finally {
					setPendingByUserId((s) => ({ ...s, [userId]: false }));
				}
			});
		},
		[schedule, updateParticipationMutation],
	);

	const isLoading = eventsLoading || participationLoading || usersLoading;

	return (
		<HockeyPlannerRoot title="Teilnehmer" showBackButton={true}>
			<Container
				maxWidth="md"
				disableGutters
				sx={{
					position: "relative",
					flex: 1,
					width: "100%",
					mx: "auto",
					my: 0,
					px: { xs: 2, sm: 3 },
					py: { xs: 2, sm: 3 },
					WebkitOverflowScrolling: "touch",
					bgcolor: "rgba(255,255,255,0.35)",
					boxShadow: 6,
					backdropFilter: "blur(4px)",
				}}
			>
				<Stack spacing={3}>
					{/* Header with event details */}
					{event && (
						<Card variant="elevation" sx={{ bgcolor: "rgba(255,255,255,0.7)" }}>
							<Box sx={{ p: 2 }}>
								<Stack direction="row" spacing={2} alignItems="flex-start">
									<Box sx={{ flex: 1 }}>
										<Typography variant="h6">Teilnehmer verwalten</Typography>
										{(() => {
											const { weekday, date, timeRange } = formatDeDateRange(
												event.date,
												event.start_time,
												event.end_time,
											);
											return (
												<Typography variant="body2" color="textSecondary">
													{weekday}, {date} • {timeRange}
												</Typography>
											);
										})()}
										<Box sx={{ mt: 2 }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												Gesamtkosten: {formatCurrency(event.total_cost_cents)}
											</Typography>
											<Typography variant="body2" color="textSecondary">
												Kosten pro Spieler: {formatCurrency(costPerPlayer)}
												{payingPlayerCount > 0 && (
													<span> ({payingPlayerCount} Spieler)</span>
												)}
											</Typography>
										</Box>
									</Box>
									<Button
										variant="contained"
										startIcon={<AddIcon />}
										onClick={() => setNewParticipantDialogOpen(true)}
										disabled={createParticipantMutation.isPending}
									>
										Neuer Spieler
									</Button>
								</Stack>
							</Box>
						</Card>
					)}

					{isLoading && (
						<Stack direction="row" alignItems="center" spacing={1}>
							<CircularProgress size={20} />
							<Typography>Wird geladen…</Typography>
						</Stack>
					)}

					{!isLoading && (
						<>
							{/* Registered users (have attendance entry) */}
							<Card
								variant="elevation"
								sx={{ bgcolor: "rgba(255,255,255,0.7)" }}
							>
								<Box sx={{ p: 2 }}>
									<Typography
										variant="subtitle1"
										gutterBottom
										sx={{ fontWeight: 600 }}
									>
										Angemeldet ({registeredUsers.length})
									</Typography>
									{registeredUsers.length > 0 ? (
										<List dense>
											{registeredUsers.map((user) => (
												<UserRow
													key={user.user_id}
													user={user}
													status={participationMap[user.user_id]}
													pending={!!pendingByUserId[user.user_id]}
													onStatusChange={(status) =>
														handleStatusChange(user.user_id, status)
													}
												/>
											))}
										</List>
									) : (
										<Typography variant="body2" color="textSecondary">
											Keine angemeldeten Spieler
										</Typography>
									)}
								</Box>
							</Card>

							{/* Unregistered users */}
							<Card
								variant="elevation"
								sx={{ bgcolor: "rgba(255,255,255,0.7)" }}
							>
								<Box sx={{ p: 2 }}>
									<Typography
										variant="subtitle1"
										gutterBottom
										sx={{ fontWeight: 600 }}
									>
										Nicht angemeldet ({unregisteredUsers.length})
									</Typography>
									{unregisteredUsers.length > 0 ? (
										<List dense>
											{unregisteredUsers.map((user) => (
												<UserRow
													key={user.user_id}
													user={user}
													status={participationMap[user.user_id]}
													pending={!!pendingByUserId[user.user_id]}
													onStatusChange={(status) =>
														handleStatusChange(user.user_id, status)
													}
												/>
											))}
										</List>
									) : (
										<Typography variant="body2" color="textSecondary">
											Alle Spieler haben sich angemeldet
										</Typography>
									)}
								</Box>
							</Card>
						</>
					)}
				</Stack>

				<Snackbar
					open={!!errorMsg}
					autoHideDuration={4000}
					onClose={() => setErrorMsg(null)}
				>
					<Alert
						onClose={() => setErrorMsg(null)}
						severity="error"
						variant="filled"
						sx={{ width: "100%" }}
					>
						{errorMsg}
					</Alert>
				</Snackbar>

				<ProfileDialog
					open={newParticipantDialogOpen}
					onClose={() => setNewParticipantDialogOpen(false)}
					title="Neuen Spieler hinzufügen"
					isCreating={true}
					onSubmit={async (formData) => {
						await createParticipantMutation.mutateAsync(formData);
						setNewParticipantDialogOpen(false);
					}}
					submitLabel="Spieler hinzufügen"
				/>
			</Container>
		</HockeyPlannerRoot>
	);
}
